import React, { useEffect, useState } from 'react';
import { Typography, useTheme } from '@material-ui/core';
import Chart, { Props as ChartProps } from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { useTranslation } from 'react-i18next';
import { useWindowSize } from '../../../hooks/windowSize';

import { useStyles } from './styles';

interface IPieChart extends ChartProps {
  values: number[];
  title: string;
  titleWidth?: string | number;
  selectedData?: any;
  showLegend: boolean;
  unit?: string;
  colors?: string[];
  seriesLabels?: string[];
}

const PieChart: React.FC<IPieChart> = ({
  values,
  title,
  titleWidth,
  selectedData,
  showLegend,
  unit,
  colors,
  seriesLabels,
  ...rest
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const size = useWindowSize();
  const theme = useTheme();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const ordersPrioritiesLabels = [
    t('components:charts.low'),
    t('components:charts.averageLabel'),
    t('components:charts.high'),
    t('components:charts.urgent'),
  ];

  const handleSelectData = (selection: any): void => {
    if (selection.selectedDataPoints[0].length === 0) {
      setSelectedIndex(null);
    } else {
      setSelectedIndex(selection.dataPointIndex);
    }
  };

  const options: ApexOptions = {
    labels: seriesLabels || ordersPrioritiesLabels,
    dataLabels: {
      enabled: false,
    },
    chart: {
      type: 'pie',
      events: {
        dataPointSelection: (event, chart, selection) =>
          handleSelectData(selection),
      },
    },
    fill: {
      opacity: 1,
    },
    stroke: {
      width: 1,
      colors: undefined,
    },
    yaxis: {
      show: false,
    },
    legend: {
      show: showLegend,
      position: 'bottom',
    },
    plotOptions: {
      polarArea: {
        rings: {
          strokeWidth: 0,
        },
        spokes: {
          strokeWidth: 0,
        },
      },
      pie: {
        donut: {
          labels: {
            show: false,
          },
        },
      },
    },
    tooltip: {
      y: {
        formatter(value: number) {
          return unit ? `${value.toFixed(1)} ${unit}` : `${value}`;
        },
      },
      style: {
        fontSize: '18px',
      },
    },
    colors: colors || [
      theme.charts.low,
      theme.charts.average,
      theme.charts.high,
      theme.charts.urgent,
    ],
  };

  useEffect(() => {
    const priorities = [
      t('components:charts.low'),
      t('components:charts.average'),
      t('components:charts.high'),
      t('components:charts.urgent'),
    ];
    if (selectedData) {
      if (selectedIndex === null) {
        selectedData(null);
      } else {
        selectedData(priorities[selectedIndex]);
      }
    }
  }, [selectedIndex, selectedData, t]);

  return (
    <div className={classes.root}>
      <Typography
        className={classes.title}
        variant="h3"
        style={{ width: titleWidth }}
      >
        {title}
      </Typography>
      <Chart
        options={options}
        series={values}
        type="pie"
        height={size.height ? size.height * 0.25 : '100%'}
        {...rest}
      />
    </div>
  );
};

export default PieChart;
